// Pure helpers for the Admin Google Reviews page (Stage 1B).
// Kept separate from the component so the logic is unit-testable in the
// repo's node-environment vitest setup (same convention as istToUtcIso.js).
import { istLocalToUtcIso } from './istToUtcIso';

export const REVIEW_PAGE_SIZE = 50;

// ─── Enum options (must match backend Stage 1A enums exactly) ─────────────────
export const STAR_RATING_OPTIONS = [5, 4, 3, 2, 1];

export const SYNC_STATUS_OPTIONS = [
  { value: 'NEW', label: 'New' },
  { value: 'SYNCED', label: 'Synced' },
  { value: 'SYNC_FAILED', label: 'Sync failed' },
  { value: 'REPLY_MODIFIED_EXTERNALLY', label: 'Reply changed on Google' },
];

export const DRAFT_STATUS_OPTIONS = [
  { value: 'NO_DRAFT', label: 'No draft' },
  { value: 'DRAFT_GENERATED', label: 'Draft ready' },
  { value: 'GENERATION_FAILED', label: 'Draft failed' },
];

export const APPROVAL_STATUS_OPTIONS = [
  { value: 'PENDING_APPROVAL', label: 'Pending approval' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'SKIPPED', label: 'Skipped' },
  { value: 'BLOCKED_BY_POLICY', label: 'Blocked by policy' },
];

export const PUBLISH_STATUS_OPTIONS = [
  { value: 'NOT_PUBLISHED', label: 'Not published' },
  { value: 'PUBLISHING', label: 'Publishing' },
  { value: 'PUBLISHED', label: 'Published' },
  { value: 'PUBLISH_FAILED', label: 'Publish failed' },
];

const LABEL_MAPS = {
  sync: Object.fromEntries(SYNC_STATUS_OPTIONS.map((o) => [o.value, o.label])),
  draft: Object.fromEntries(DRAFT_STATUS_OPTIONS.map((o) => [o.value, o.label])),
  approval: Object.fromEntries(APPROVAL_STATUS_OPTIONS.map((o) => [o.value, o.label])),
  publish: Object.fromEntries(PUBLISH_STATUS_OPTIONS.map((o) => [o.value, o.label])),
};

export function syncStatusLabel(v) { return LABEL_MAPS.sync[v] ?? v ?? '—'; }
export function draftStatusLabel(v) { return LABEL_MAPS.draft[v] ?? v ?? '—'; }
export function approvalStatusLabel(v) { return LABEL_MAPS.approval[v] ?? v ?? '—'; }
export function publishStatusLabel(v) { return LABEL_MAPS.publish[v] ?? v ?? '—'; }

// ─── Status display ────────────────────────────────────────────────────────────
const CONNECTION_LABELS = {
  UNCONFIGURED: 'Not connected',
  PENDING_CONFIGURATION: 'Setup in progress',
  CONNECTED: 'Connected',
  REAUTH_REQUIRED: 'Reconnect needed',
  DISCONNECTED: 'Disconnected',
  ERROR: 'Error',
};

export function connectionStatusLabel(status) {
  return CONNECTION_LABELS[status] ?? status ?? '—';
}

export function isGoogleConnected(status) {
  return status === 'CONNECTED';
}

const PROVIDER_LABELS = {
  CONFIGURED: 'Configured',
  GOOGLE_REVIEWS_NOT_CONFIGURED: 'Not configured',
  GOOGLE_REVIEWS_PROVIDER_UNAVAILABLE: 'Unavailable',
  REVIEW_AI_NOT_CONFIGURED: 'Not configured',
  UNCONFIGURED: 'Not configured',
};

export function providerStatusLabel(code) {
  return PROVIDER_LABELS[code] ?? code ?? '—';
}

// The backend reports google_provider_status as CONFIGURED only when the OAuth
// client id/secret/callback are all present server-side.
export function isGoogleProviderConfigured(status) {
  return status?.google_provider_status === 'CONFIGURED';
}

export const GOOGLE_NOT_CONNECTED_MESSAGE =
  'Google Business Profile is not connected yet. Reviews cannot be synchronized or replied to.';

// ─── Review list params ────────────────────────────────────────────────────────
// Dates arrive as yyyy-mm-dd from date inputs and are interpreted as IST wall
// time: From = start of day, To = end of day. Blank filters are omitted.
export function buildReviewParams({
  page = 0,
  starRating = '',
  syncStatus = '',
  draftStatus = '',
  approvalStatus = '',
  publishStatus = '',
  fromDate = '',
  toDate = '',
} = {}) {
  const params = {
    limit: REVIEW_PAGE_SIZE,
    offset: Math.max(0, Number(page) || 0) * REVIEW_PAGE_SIZE,
  };
  const rating = Number(starRating);
  if (Number.isInteger(rating) && rating >= 1 && rating <= 5) params.star_rating = rating;
  if (syncStatus) params.sync_status = syncStatus;
  if (draftStatus) params.draft_status = draftStatus;
  if (approvalStatus) params.approval_status = approvalStatus;
  if (publishStatus) params.publish_status = publishStatus;
  const fromIso = fromDate ? istLocalToUtcIso(`${fromDate}T00:00`) : '';
  const toIso = toDate ? istLocalToUtcIso(`${toDate}T23:59`) : '';
  if (fromIso) params.date_from = fromIso;
  if (toIso) params.date_to = toIso;
  return params;
}

// ─── Settings form ─────────────────────────────────────────────────────────────
// Only these fields may ever be sent to PATCH /settings. automatic_publishing
// stays disabled in Stage 1A/1B and ai_provider_status is read-only, so both
// are deliberately excluded here.
const EDITABLE_FIELDS = [
  'automation_enabled',
  'emergency_pause',
  'polling_interval_minutes',
  'positive_review_min_stars',
  'manual_approval_max_stars',
  'business_display_name',
  'support_email',
];

export function settingsToForm(settings) {
  if (!settings) return null;
  return {
    automation_enabled: !!settings.automation_enabled,
    emergency_pause: !!settings.emergency_pause,
    polling_interval_minutes: String(settings.polling_interval_minutes ?? ''),
    positive_review_min_stars: String(settings.positive_review_min_stars ?? ''),
    manual_approval_max_stars: String(settings.manual_approval_max_stars ?? ''),
    business_display_name: settings.business_display_name ?? '',
    support_email: settings.support_email ?? '',
  };
}

function parseIntField(value) {
  if (value === '' || value == null) return NaN;
  return Number(value);
}

// Returns { field: message } — empty object means valid.
export function validateSettings(form) {
  const errors = {};
  if (!form) return errors;

  const polling = parseIntField(form.polling_interval_minutes);
  if (!Number.isInteger(polling) || polling < 15 || polling > 1440) {
    errors.polling_interval_minutes = 'Polling interval must be a whole number between 15 and 1440 minutes.';
  }

  const positive = parseIntField(form.positive_review_min_stars);
  if (!Number.isInteger(positive) || positive < 1 || positive > 5) {
    errors.positive_review_min_stars = 'Positive review threshold must be between 1 and 5 stars.';
  }

  const manual = parseIntField(form.manual_approval_max_stars);
  if (!Number.isInteger(manual) || manual < 1 || manual > 5) {
    errors.manual_approval_max_stars = 'Manual approval threshold must be between 1 and 5 stars.';
  }

  if (
    Number.isInteger(positive) && Number.isInteger(manual)
    && positive >= 1 && positive <= 5 && manual >= 1 && manual <= 5
    && manual >= positive
  ) {
    errors.manual_approval_max_stars =
      'Manual approval maximum must be lower than the positive review threshold.';
  }

  const name = (form.business_display_name ?? '').trim();
  if (!name) {
    errors.business_display_name = 'Business name is required.';
  } else if (name.length > 180) {
    errors.business_display_name = 'Business name must be 180 characters or fewer.';
  }

  const email = (form.support_email ?? '').trim();
  if (email) {
    if (email.length > 254) {
      errors.support_email = 'Support email must be 254 characters or fewer.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.support_email = 'Enter a valid email address.';
    }
  }

  return errors;
}

// Builds the PATCH body: only editable fields that actually changed, with
// trimmed strings and numeric conversions. Never includes
// automatic_publishing_enabled or ai_provider_status.
export function buildSettingsPayload(form, originalSettings) {
  if (!form || !originalSettings) return {};
  const original = settingsToForm(originalSettings);
  const payload = {};

  for (const field of EDITABLE_FIELDS) {
    if (form[field] === original[field]) continue;
    switch (field) {
      case 'automation_enabled':
      case 'emergency_pause':
        payload[field] = !!form[field];
        break;
      case 'polling_interval_minutes':
      case 'positive_review_min_stars':
      case 'manual_approval_max_stars':
        payload[field] = Number(form[field]);
        break;
      case 'business_display_name':
        payload[field] = (form[field] ?? '').trim();
        break;
      case 'support_email': {
        const trimmed = (form[field] ?? '').trim();
        payload[field] = trimmed === '' ? null : trimmed;
        break;
      }
      default:
        break;
    }
  }
  return payload;
}

// ─── Manual sync ───────────────────────────────────────────────────────────────
export function newSyncIdempotencyKey() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `manual-sync-${crypto.randomUUID()}`;
  }
  // Fallback for environments without randomUUID (still unique per click).
  return `manual-sync-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

// Maps a sync attempt response to user-facing feedback. Messages are our own
// friendly wording keyed by the backend's sanitized error codes — raw backend
// exception text is never shown.
const SYNC_ERROR_MESSAGES = {
  GOOGLE_REVIEWS_SYNC_IN_PROGRESS: { type: 'info', message: 'A sync is already running for the selected location. Try again in a moment.' },
  GOOGLE_REFRESH_IN_PROGRESS: { type: 'info', message: 'Google authorization is being refreshed. Try again in a moment.' },
  GOOGLE_REVIEWS_EMERGENCY_PAUSED: { type: 'info', message: 'Imports are paused. Turn off Emergency pause in Settings, then try again.' },
  GOOGLE_REVIEWS_NOT_CONNECTED: { type: 'info', message: 'Google is not connected. Connect Google Business Profile first.' },
  GOOGLE_REVIEWS_LOCATION_NOT_SELECTED: { type: 'info', message: 'Select exactly one location before syncing reviews.' },
  GOOGLE_AUTH_REAUTH_REQUIRED: { type: 'error', message: 'Google authorization needs to be renewed. Reconnect Google, then try again.' },
  GOOGLE_ACCESS_FORBIDDEN: { type: 'error', message: 'Google denied access to this business profile. Reconnect Google and check permissions, then try again.' },
  GOOGLE_LOCATION_NOT_FOUND: { type: 'error', message: 'Google could not find the selected location. Run Discover Locations again.' },
  GOOGLE_RATE_LIMITED: { type: 'info', message: 'Google is limiting requests right now. Try again later.' },
  GOOGLE_REQUEST_TIMEOUT: { type: 'error', message: 'Google did not respond in time. Try again.' },
  GOOGLE_REQUEST_UNAVAILABLE: { type: 'error', message: 'Google could not be reached. Check the connection and try again.' },
};

export function formatSyncSummary(summary) {
  if (!summary || typeof summary !== 'object') return '';
  const parts = [
    `${Number(summary.created) || 0} new`,
    `${Number(summary.updated) || 0} updated`,
    `${Number(summary.unchanged) || 0} unchanged`,
  ];
  const skipped = Number(summary.skipped) || 0;
  if (skipped > 0) parts.push(`${skipped} skipped`);
  parts.push(`${Number(summary.pages_fetched) || 0} page${Number(summary.pages_fetched) === 1 ? '' : 's'} fetched`);
  let text = `Sync complete: ${parts.join(' · ')}.`;
  if (summary.next_page_token_present) {
    text += ' More reviews remain — sync again to import them.';
  }
  return text;
}

export function getSyncFeedback(attempt) {
  if (!attempt || !attempt.status) return null;
  switch (attempt.status) {
    case 'BLOCKED_UNCONFIGURED':
      return { type: 'info', message: GOOGLE_NOT_CONNECTED_MESSAGE };
    case 'FAILED': {
      const mapped = SYNC_ERROR_MESSAGES[attempt.sanitized_error_code];
      if (mapped) return mapped;
      return { type: 'error', message: 'Review sync failed. Please try again later.' };
    }
    case 'COMPLETED':
      return { type: 'success', message: formatSyncSummary(attempt.result_summary) || 'Review synchronization completed.' };
    case 'QUEUED':
    case 'CLAIMED':
      return { type: 'info', message: 'Sync request recorded. Reviews will update after the sync runs.' };
    default:
      return { type: 'info', message: `Sync status: ${attempt.status}` };
  }
}

// ─── Locations ─────────────────────────────────────────────────────────────────
export function getActiveLocation(items) {
  const active = (Array.isArray(items) ? items : []).filter((l) => l?.is_active);
  return { activeLocation: active.length === 1 ? active[0] : null, activeCount: active.length };
}

// ─── Sync enablement ───────────────────────────────────────────────────────────
export const SYNC_DISABLED_MESSAGES = {
  provider_not_configured: 'Sync is unavailable because the Google provider is not configured yet.',
  not_connected: 'Connect Google Business Profile before syncing reviews.',
  reauth_required: 'Reconnect Google before syncing reviews. Your authorization needs to be renewed.',
  connection_error: 'There is a connection problem. Reconnect Google before syncing.',
  no_active_location: 'Select exactly one location before syncing reviews.',
  emergency_pause: 'Imports are paused. Turn off Emergency pause in Settings to sync.',
  sync_running: 'A sync request is already running.',
};

export function getSyncAvailability({
  providerConfigured,
  connectionStatus,
  activeCount,
  emergencyPause,
  syncPending,
}) {
  if (!providerConfigured) return { enabled: false, reasonKey: 'provider_not_configured' };
  if (connectionStatus === 'REAUTH_REQUIRED') return { enabled: false, reasonKey: 'reauth_required' };
  if (connectionStatus === 'ERROR') return { enabled: false, reasonKey: 'connection_error' };
  if (connectionStatus !== 'CONNECTED') return { enabled: false, reasonKey: 'not_connected' };
  if (activeCount !== 1) return { enabled: false, reasonKey: 'no_active_location' };
  if (emergencyPause) return { enabled: false, reasonKey: 'emergency_pause' };
  if (syncPending) return { enabled: false, reasonKey: 'sync_running' };
  return { enabled: true, reasonKey: null };
}

// ─── OAuth start / callback ────────────────────────────────────────────────────
// Validates the OAuth start response and returns the authorization URL to
// navigate to. Never logs or persists the URL (it carries OAuth state).
export function extractAuthorizationUrl(response) {
  const url = response?.authorization_url;
  if (typeof url !== 'string' || !url.startsWith('https://')) return null;
  return url;
}

const OAUTH_ERROR_MESSAGES = {
  provider_denied: 'Google sign-in was cancelled or denied. No connection was made.',
  invalid_state: 'The sign-in session expired or was invalid. Try connecting again.',
  invalid_callback: 'Google sign-in could not be completed. Try connecting again.',
  oauth_failed: 'Google sign-in failed. Try connecting again.',
  google_token_exchange_failed: 'Google sign-in could not be completed. Try connecting again.',
  google_auth_reauth_required: 'Google authorization needs to be renewed. Try connecting again.',
  google_access_forbidden: 'Google denied access. Check the account permissions and try again.',
  google_rate_limited: 'Google is limiting requests right now. Wait a moment and try again.',
  google_request_timeout: 'Google did not respond in time. Try connecting again.',
  google_request_unavailable: 'Google could not be reached. Check the connection and try again.',
};

const OAUTH_SUCCESS_PARAM = 'google_reviews_oauth';
const OAUTH_ERROR_PARAM = 'google_reviews_oauth_error';

// Reads only the two documented callback params. Raw query-string content is
// never displayed; unknown codes fall back to a generic message.
export function getOAuthCallbackResult(search) {
  const params = new URLSearchParams(typeof search === 'string' ? search : '');
  if (params.get(OAUTH_SUCCESS_PARAM) === 'connected') {
    return {
      type: 'success',
      message: 'Google Business Profile connected. You can now discover locations and sync reviews.',
    };
  }
  const code = params.get(OAUTH_ERROR_PARAM);
  if (code) {
    return {
      type: 'error',
      message: OAUTH_ERROR_MESSAGES[code] ?? 'Google sign-in failed. Try connecting again.',
    };
  }
  return null;
}

// Returns the query string with only the OAuth callback params removed, or
// null when there was nothing to remove. Unrelated params are preserved.
export function stripOAuthParams(search) {
  const params = new URLSearchParams(typeof search === 'string' ? search : '');
  if (!params.has(OAUTH_SUCCESS_PARAM) && !params.has(OAUTH_ERROR_PARAM)) return null;
  params.delete(OAUTH_SUCCESS_PARAM);
  params.delete(OAUTH_ERROR_PARAM);
  return params.toString();
}

// ─── Discover / connection error mapping ───────────────────────────────────────
export function getDiscoverError(err) {
  if (err?.status === 503) return 'Google provider not configured.';
  if (err?.status === 409) return 'Connect Google Business Profile first, then discover locations.';
  return 'Could not discover locations. Try again.';
}

export function getConnectError(err) {
  if (err?.status === 503) return 'Google provider not configured.';
  return 'Could not start Google sign-in. Try again.';
}

// ─── Review row helpers ────────────────────────────────────────────────────────
export function excerpt(text, max = 120) {
  if (!text) return '';
  const clean = String(text).trim();
  return clean.length > max ? `${clean.slice(0, max - 1)}…` : clean;
}
